import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingCart, 
  faUser, 
  faSearch, 
  faBars, 
  faTimes,
  faHeart,
  faLanguage
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { theme } from '../../styles/theme';
import { Container } from '../../styles/components';
import LanguageSwitcher from './LanguageSwitcher';

// Import logo image
// Note: You need to download the logo from the Google Drive link and place it in this path
import logoImage from '../../assets/images/logo.png';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeaderContainer = styled.header<{ isScrolled: boolean }>`
  background-color: ${({ isScrolled }) => 
    isScrolled ? theme.colors.background : 'rgba(255, 255, 255, 0.85)'};
  box-shadow: ${({ isScrolled }) => 
    isScrolled ? theme.shadows.medium : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${theme.zIndex.sticky};
  transition: ${theme.transitions.medium};
  transform: translateZ(0);
  width: 100%;
  backdrop-filter: ${({ isScrolled }) => isScrolled ? 'none' : 'blur(5px)'};
`;

const TopBar = styled.div<{ isScrolled: boolean }>`
  background: ${theme.colors.secondary};
  color: ${theme.colors.background};
  text-align: center;
  padding: ${theme.spacing.xs} 0;
  font-size: 0.9rem;
  transition: ${theme.transitions.medium};
  height: ${({ isScrolled }) => (isScrolled ? '0' : 'auto')};
  overflow: hidden;
  opacity: ${({ isScrolled }) => (isScrolled ? '0' : '1')};
  
  span {
    display: inline-block;
    padding: 0 ${theme.spacing.md};
    font-weight: 300;
  }
  
  span.separator {
    opacity: 0.5;
    padding: 0 ${theme.spacing.xs};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    padding: ${theme.spacing.xxs} 0;
    white-space: nowrap;
    overflow-x: auto;
    
    span {
      padding: 0 ${theme.spacing.sm};
    }
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const HeaderInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 ${theme.spacing.md};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.sm};
  }
`;

const MainHeader = styled.div<{ isScrolled: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ isScrolled }) =>
    isScrolled
      ? `${theme.spacing.md} 0`
      : `${theme.spacing.md} 0`};
  transition: ${theme.transitions.medium};
`;

const Logo = styled(Link)<{ isScrolled: boolean }>`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: ${theme.spacing.xl};
  transition: ${theme.transitions.medium};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-right: ${theme.spacing.md};
  }
`;

const LogoImage = styled.img<{ isScrolled: boolean }>`
  height: ${({ isScrolled }) => (isScrolled ? '40px' : '50px')};
  transition: ${theme.transitions.medium};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: ${({ isScrolled }) => (isScrolled ? '32px' : '40px')};
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Nav = styled.nav<{ 
  isOpen: boolean;
  isScrolled: boolean;
}>`
  display: flex;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 75%;
    max-width: 300px;
    background-color: ${theme.colors.background};
    box-shadow: ${theme.shadows.large};
    padding: ${theme.spacing.xl};
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
    transition: transform 0.3s ease-in-out;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 85%;
    padding: ${theme.spacing.lg};
  }
`;

const NavList = styled.ul<{ isScrolled: boolean }>`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    margin-top: ${theme.spacing.xl};
    align-items: flex-start;
    width: 100%;
  }
`;

const NavItem = styled.li<{ isActive: boolean; isScrolled: boolean }>`
  position: relative;
  padding: 0;
  
  a {
    color: ${({ isScrolled }) =>
      isScrolled ? theme.colors.secondary : theme.colors.secondary};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    padding: ${theme.spacing.sm} ${theme.spacing.sm};
    display: block;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    position: relative;
    white-space: nowrap;
    
    &:hover {
      color: ${theme.colors.primary};
    }
    
    ${({ isActive }) =>
      isActive &&
      css`
        color: ${theme.colors.primary} !important;
        font-weight: 600;
        
        &:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: ${theme.colors.primary};
          animation: ${fadeIn} 0.3s forwards;
        }
      `}
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    a {
      color: ${theme.colors.secondary};
      padding: ${theme.spacing.md} ${theme.spacing.sm};
      font-size: 1.1rem;
      width: 100%;
    }
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const SearchContainer = styled.div<{ isActive: boolean; isScrolled: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${({ isActive, isScrolled }) =>
    isActive
      ? 'rgba(0, 0, 0, 0.1)'
      : isScrolled
      ? 'rgba(0, 0, 0, 0.05)'
      : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 20px;
  padding: 6px 15px;
  margin-right: ${theme.spacing.lg};
  transition: ${theme.transitions.medium};
  width: ${({ isActive }) => (isActive ? '200px' : '160px')};
  border: 1px solid ${({ isScrolled }) => 
    isScrolled ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  
  &:focus-within {
    background-color: ${({ isScrolled }) =>
      isScrolled ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0.07)'};
    width: 220px;
    border-color: ${({ isScrolled }) => 
    isScrolled ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SearchInput = styled.input<{ isScrolled: boolean }>`
  border: none;
  background: transparent;
  color: ${({ isScrolled }) =>
    isScrolled ? theme.colors.secondary : theme.colors.secondary};
  font-size: 0.9rem;
  width: 100%;
  
  &::placeholder {
    color: ${({ isScrolled }) =>
      isScrolled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }
  
  &:focus {
    outline: none;
  }
`;

const SearchIcon = styled.button<{ isScrolled: boolean }>`
  background: none;
  border: none;
  color: ${({ isScrolled }) =>
    isScrolled ? theme.colors.secondary : theme.colors.secondary};
  cursor: pointer;
  transition: ${theme.transitions.short};
  opacity: 0.7;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 1;
    color: ${theme.colors.primary};
  }
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

const IconButton = styled(Link)<{ isScrolled: boolean }>`
  color: ${({ isScrolled }) =>
    isScrolled ? theme.colors.secondary : theme.colors.secondary};
  font-size: 1.2rem;
  position: relative;
  transition: ${theme.transitions.short};
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.round};
  margin: 0 5px;
  
  &:hover {
    color: ${theme.colors.primary};
    transform: translateY(-2px);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    color: ${theme.colors.secondary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    margin: 0 2px;
  }
`;

const CartCount = styled.span<{ isEmpty: boolean }>`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${({ isEmpty }) => isEmpty ? 'transparent' : theme.colors.primary};
  color: ${theme.colors.secondary};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: ${theme.transitions.medium};
  transform-origin: center;
  animation: ${({ isEmpty }) => isEmpty ? 'none' : 'pulse 1s'};
  
  @keyframes pulse {
    0% { transform: scale(0); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
`;

const HamburgerButton = styled.button<{ isScrolled: boolean }>`
  display: none;
  background: none;
  border: none;
  color: ${({ isScrolled }) => 
    isScrolled ? theme.colors.secondary : theme.colors.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${theme.spacing.xs};
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${theme.colors.secondary};
  cursor: pointer;
  transition: ${theme.transitions.short};
  
  &:hover {
    color: ${theme.colors.primary};
    transform: rotate(90deg);
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
    font-size: 1.2rem;
  }
`;

const Backdrop = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  transition: opacity 0.3s ease-in-out;
  backdrop-filter: blur(3px);
`;

// Define simplified navigation items to avoid duplicates
const NAVIGATION_ITEMS = [
  { path: '/', label: 'header.home' },
  { path: '/products', label: 'header.products' },
  { path: '/about', label: 'header.aboutUs' },
  { path: '/contact', label: 'header.contactUs' },
];

const Header: React.FC = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const { totalItems } = useCart();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      // Check if we're on the homepage to apply transparent header
      const isHomePage = location.pathname === '/';
      const scrollPosition = window.scrollY;
      
      if (isHomePage) {
        setIsScrolled(scrollPosition > 50);
      } else {
        setIsScrolled(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);
  
  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };
  
  const closeNav = () => {
    setIsNavOpen(false);
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement search functionality
    console.log('Searching for:', searchTerm);
    setSearchTerm('');
    setIsSearchActive(false);
  };
  
  // Check if a nav item is active
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <HeaderContainer isScrolled={isScrolled}>
      <TopBar isScrolled={isScrolled}>
        <span>{t('Free shipping on orders over $100')}</span>
        <span className="separator">•</span>
        <span>{t('Same day delivery in Manama')}</span>
        <span className="separator">•</span>
        <span>{t('Premium Quality Guaranteed')}</span>
      </TopBar>
      <HeaderInner>
        <MainHeader isScrolled={isScrolled}>
          <Logo to="/" isScrolled={isScrolled}>
            <LogoImage src={logoImage} alt="Alherz Perfume" isScrolled={isScrolled} />
          </Logo>
          
          <NavContainer>
            <Nav isOpen={isNavOpen} isScrolled={isScrolled}>
              <CloseButton onClick={closeNav}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
              <NavList isScrolled={isScrolled}>
                {NAVIGATION_ITEMS.map((item) => (
                  <NavItem 
                    key={item.path} 
                    isActive={isActive(item.path)}
                    isScrolled={isScrolled}
                  >
                    <Link to={item.path} onClick={closeNav}>
                      {t(item.label)}
                    </Link>
                  </NavItem>
                ))}
              </NavList>
            </Nav>
            
            <ActionsContainer>
              <SearchContainer 
                isScrolled={isScrolled}
                isActive={isSearchActive}
                onFocus={() => setIsSearchActive(true)}
                onBlur={() => setIsSearchActive(false)}
              >
                <SearchInput 
                  type="text" 
                  placeholder={t('header.search')} 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  isScrolled={isScrolled}
                />
                <SearchIcon 
                  onClick={handleSearch}
                  isScrolled={isScrolled}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
              </SearchContainer>
              
              <IconsContainer>
                <LanguageSwitcher />
                <IconButton to="/wishlist" isScrolled={isScrolled}>
                  <FontAwesomeIcon icon={faHeart} />
                </IconButton>
                <IconButton to="/cart" isScrolled={isScrolled}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                  <CartCount isEmpty={totalItems === 0}>{totalItems > 0 ? totalItems : ''}</CartCount>
                </IconButton>
                <IconButton to="/account" isScrolled={isScrolled}>
                  <FontAwesomeIcon icon={faUser} />
                </IconButton>
                <HamburgerButton 
                  onClick={toggleNav}
                  isScrolled={isScrolled}
                >
                  <FontAwesomeIcon icon={faBars} />
                </HamburgerButton>
              </IconsContainer>
            </ActionsContainer>
          </NavContainer>
        </MainHeader>
      </HeaderInner>
      
      <Backdrop isVisible={isNavOpen} onClick={closeNav} />
    </HeaderContainer>
  );
};

export default Header; 